import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, Send, CheckCircle, XCircle, ArrowUpRight, ArrowDownLeft, Clock, History, ChevronDown, User, AlertTriangle, Building } from 'lucide-react';
import { employeeApi } from '../../api/employeeApi';
import './Dashboard.css';
import './CashTransfers.css';

const CashTransfers = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('PENDING');

    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);

    // Modals & Forms
    const [showSendModal, setShowSendModal] = useState(false);
    const [showAdminSendModal, setShowAdminSendModal] = useState(false); // NEW
    const [directory, setDirectory] = useState([]);
    const [adminDirectory, setAdminDirectory] = useState([]);
    const [sendForm, setSendForm] = useState({ toEmployeeId: '', amount: '', reason: '' });
    const [sending, setSending] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Custom Confirmation & Info Modals
    const [confirmModal, setConfirmModal] = useState({ show: false, action: null, transferId: null, title: '', message: '' });
    const [actionProcessing, setActionProcessing] = useState(false);
    const [infoModal, setInfoModal] = useState({ show: false, title: '', message: '', isError: false }); // NEW

    useEffect(() => {
        fetchTransfers(activeTab);
    }, [activeTab]);

    const fetchTransfers = async (tab) => {
        setLoading(true);
        try {
            const res = await employeeApi.cashTransfers.getHistory({ limit: 50 });
            if (res.data.success) {
                const allTx = res.data.data.transfers;
                if (tab === 'PENDING') {
                    setTransactions(allTx.filter(tx => tx.status === 'PENDING'));
                } else {
                    setTransactions(allTx.filter(tx => tx.status !== 'PENDING'));
                }
            }
        } catch (err) {
            console.error("Failed to load transfers", err);
        } finally {
            setLoading(false);
        }
    };

    const openSendModal = async () => {
        setShowSendModal(true);
        setDropdownOpen(false);
        try {
            const res = await employeeApi.cashTransfers.getDirectory();
            if (res.data.success) {
                setDirectory(res.data.data.employees);
            }
        } catch (err) {
            setInfoModal({ show: true, title: "Directory Error", message: "Failed to load employee directory", isError: true });
        }
    };

    const openAdminSendModal = async () => {
        setSendForm({ toEmployeeId: '', amount: '', reason: '' });
        setShowAdminSendModal(true);
        setDropdownOpen(false);
        try {
            const res = await employeeApi.cashTransfers.getAdminDirectory();
            if (res.data.success) {
                setAdminDirectory(res.data.data.admins);
            }
        } catch (err) {
            setInfoModal({ show: true, title: "Directory Error", message: "Failed to load admin directory", isError: true });
        }
    };

    // Unified Send Function for both Employee and Admin
    const handleSendCash = async (e, isToAdmin = false) => {
        e.preventDefault();

        if (!isToAdmin && !sendForm.toEmployeeId) {
            setInfoModal({ show: true, title: "Missing Recipient", message: "Please select an employee first.", isError: true });
            return;
        }

        setSending(true);
        try {
            if (isToAdmin) {
                await employeeApi.cashTransfers.initiateToAdmin({
                    toAdminId: sendForm.toEmployeeId,
                    amount: Number(sendForm.amount),
                    reason: sendForm.reason
                });
            } else {
                await employeeApi.cashTransfers.initiate({
                    toEmployeeId: sendForm.toEmployeeId,
                    amount: Number(sendForm.amount),
                    reason: sendForm.reason
                });
            }

            setShowSendModal(false);
            setShowAdminSendModal(false);
            setSendForm({ toEmployeeId: '', amount: '', reason: '' });
            fetchTransfers(activeTab);
            setInfoModal({ show: true, title: "Transfer Initiated", message: "Your cash transfer request has been sent successfully.", isError: false });
        } catch (err) {
            setInfoModal({ show: true, title: "Transfer Failed", message: err.response?.data?.message || "Failed to send cash", isError: true });
        } finally {
            setSending(false);
        }
    };

    const handleActionClick = (transferId, action) => {
        const isConfirm = action === 'confirm';
        setConfirmModal({
            show: true,
            action,
            transferId,
            title: isConfirm ? 'Confirm Receipt' : 'Cancel Transfer',
            message: isConfirm
                ? 'Are you sure you want to confirm receipt of this cash? Your recorded Cash in Hand will increase.'
                : 'Are you sure you want to cancel this transfer request?'
        });
    };

    const executeAction = async () => {
        const { action, transferId } = confirmModal;
        setActionProcessing(true);

        try {
            if (action === 'confirm') {
                await employeeApi.cashTransfers.confirm(transferId);
            } else {
                await employeeApi.cashTransfers.cancel(transferId);
            }
            setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' });
            fetchTransfers(activeTab);
        } catch (err) {
            setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' });
            setInfoModal({ show: true, title: "Action Failed", message: err.response?.data?.message || `Failed to ${action} transfer`, isError: true });
        } finally {
            setActionProcessing(false);
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
    // Switch between directories so the dropdown UI shows the correct selected name
    const selectedEmployeeObj = showAdminSendModal
        ? adminDirectory.find(admin => admin._id === sendForm.toEmployeeId)
        : directory.find(emp => emp._id === sendForm.toEmployeeId);

    return (
        <div className="emp-dash-wrapper">
            <header className="emp-header">
                <div className="header-left">
                    <button className="elder-back-btn" onClick={() => navigate('/employee/dashboard')}>
                        <ArrowLeft size={24} /> <span>Back</span>
                    </button>
                </div>
                <div className="header-center">
                    <h1 className="page-title">Wallet & Cash Transfers</h1>
                </div>
                <div className="header-right"></div>
            </header>

            <main className="emp-main-content">

                <div className="wallet-controls">
                    <div className="wallet-tabs">
                        <button className={`w-tab ${activeTab === 'PENDING' ? 'active' : ''}`} onClick={() => setActiveTab('PENDING')}>
                            <Clock size={18} /> Pending Actions
                        </button>
                        <button className={`w-tab ${activeTab === 'HISTORY' ? 'active' : ''}`} onClick={() => setActiveTab('HISTORY')}>
                            <History size={18} /> Transfer History
                        </button>
                    </div>
                    <div className="wallet-actions-group">
                        <button className="emp-btn-secondary send-cash-btn" onClick={openAdminSendModal}>
                            <Building size={18} /> Send to Admin
                        </button>
                        <button className="emp-btn-primary send-cash-btn" onClick={openSendModal}>
                            <Send size={18} /> Send to Employee
                        </button>
                    </div>
                </div>

                <div className="wallet-list-container">
                    {loading ? (
                        <div className="wallet-empty"><div className="spinner"></div></div>
                    ) : transactions.length === 0 ? (
                        <div className="wallet-empty">
                            <Wallet size={48} opacity={0.3} />
                            <p>No {activeTab.toLowerCase()} transfers found.</p>
                        </div>
                    ) : (
                        <div className="wallet-feed">
                            {transactions.map(tx => {
                                const isSentByMe = tx.direction === 'SENT';

                                return (
                                    <div key={tx._id} className="wallet-card">
                                        <div className="w-card-left">
                                            <div className={`w-icon ${isSentByMe ? 'bg-blue-light text-blue' : 'bg-emerald-light text-emerald'}`}>
                                                {isSentByMe ? <ArrowUpRight size={24} /> : <ArrowDownLeft size={24} />}
                                            </div>
                                            <div className="w-info">
                                                <h4>{isSentByMe ? `Sent to ${tx.to.name}` : `Incoming from ${tx.from.name}`}</h4>
                                                <p>{new Date(tx.createdAt).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                {tx.reason && <p className="w-reason">"{tx.reason}"</p>}
                                            </div>
                                        </div>

                                        <div className="w-card-right">
                                            <div className="w-amount-block">
                                                <h3 className={isSentByMe ? 'text-blue' : 'text-emerald'}>
                                                    {formatCurrency(tx.amount)}
                                                </h3>
                                                {activeTab === 'HISTORY' && (
                                                    <span className={`w-status ${tx.status === 'CONFIRMED' ? 's-green' : tx.status === 'REJECTED' ? 's-red' : 's-gray'}`}>
                                                        {tx.status}
                                                    </span>
                                                )}
                                            </div>

                                            {activeTab === 'PENDING' && (
                                                <div className="w-actions">
                                                    {isSentByMe ? (
                                                        <button className="tx-action-btn btn-cancel" onClick={() => handleActionClick(tx._id, 'cancel')}>
                                                            <XCircle size={18} /> Cancel Request
                                                        </button>
                                                    ) : (
                                                        <button className="tx-action-btn btn-confirm" onClick={() => handleActionClick(tx._id, 'confirm')}>
                                                            <CheckCircle size={18} /> Confirm Receipt
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            {/* Custom Confirmation Dialog Modal */}
            {confirmModal.show && (
                <div className="emp-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="emp-modal-card confirm-dialog-card">
                        <div className={`confirm-icon-wrapper ${confirmModal.action === 'confirm' ? 'bg-emerald-light text-emerald' : 'bg-red-light text-red'}`}>
                            {confirmModal.action === 'confirm' ? <CheckCircle size={36} /> : <AlertTriangle size={36} />}
                        </div>
                        <h3>{confirmModal.title}</h3>
                        <p>{confirmModal.message}</p>

                        <div className="modal-actions" style={{ marginTop: '1.5rem', width: '100%' }}>
                            <button
                                type="button"
                                className="emp-btn-secondary"
                                onClick={() => setConfirmModal({ show: false, action: null, transferId: null, title: '', message: '' })}
                                disabled={actionProcessing}
                            >
                                No, Keep it
                            </button>
                            <button
                                type="button"
                                className={`emp-btn-primary ${confirmModal.action === 'cancel' ? 'btn-danger-fill' : 'btn-success-fill'}`}
                                onClick={executeAction}
                                disabled={actionProcessing}
                            >
                                {actionProcessing ? 'Processing...' : `Yes, ${confirmModal.action === 'confirm' ? 'Confirm' : 'Cancel'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Send Cash to EMPLOYEE Modal --- */}
            {showSendModal && (
                <div className="emp-modal-overlay">
                    <div className="emp-modal-card">
                        <h3>Send to Employee</h3>
                        <p style={{ marginBottom: '1.5rem', color: '#64748b' }}>Transfer physical cash to another approved employee.</p>

                        <form onSubmit={(e) => handleSendCash(e, false)}>
                            <div className="form-group custom-dropdown-container" style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Select Employee</label>

                                <div
                                    className={`custom-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    {selectedEmployeeObj ? (
                                        <div className="selected-emp-display">
                                            <div className="emp-avatar-mini"><User size={16} /></div>
                                            <span className="selected-emp-name">{selectedEmployeeObj.name}</span>
                                        </div>
                                    ) : (
                                        <span className="placeholder-text">-- Choose an employee --</span>
                                    )}
                                    <ChevronDown size={20} className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''}`} />
                                </div>

                                {dropdownOpen && (
                                    <div className="custom-dropdown-menu">
                                        {directory.length === 0 ? (
                                            <div className="dropdown-empty">No other employees found.</div>
                                        ) : (
                                            directory.map(emp => (
                                                <div
                                                    key={emp._id}
                                                    className={`custom-dropdown-item ${sendForm.toEmployeeId === emp._id ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setSendForm({ ...sendForm, toEmployeeId: emp._id });
                                                        setDropdownOpen(false);
                                                    }}
                                                >
                                                    <div className="emp-avatar-mini"><User size={16} /></div>
                                                    <div className="emp-drop-info">
                                                        <h4>{emp.name}</h4>
                                                        <span>{emp.phoneNumber}</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Amount (₹)</label>
                                <input
                                    type="number"
                                    className="emp-select-input"
                                    min="1"
                                    placeholder="0"
                                    value={sendForm.amount}
                                    onChange={e => setSendForm({ ...sendForm, amount: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '2rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Reason (Optional)</label>
                                <input
                                    type="text"
                                    className="emp-select-input"
                                    placeholder="e.g. Short on cash for payout"
                                    value={sendForm.reason}
                                    onChange={e => setSendForm({ ...sendForm, reason: e.target.value })}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="emp-btn-secondary" onClick={() => setShowSendModal(false)}>Cancel</button>
                                <button type="submit" className="emp-btn-primary send-cash-btn" disabled={sending}>
                                    {sending ? 'Sending...' : 'Initiate Transfer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- Send Cash to ADMIN Modal --- */}
            {showAdminSendModal && (
                <div className="emp-modal-overlay">
                    <div className="emp-modal-card">
                        <div className="modal-icon-wrapper bg-blue-light text-blue" style={{ margin: '0 auto 1rem auto' }}>
                            <Building size={32} />
                        </div>
                        <h3 style={{ textAlign: 'center' }}>Send to Admin</h3>
                        <p style={{ marginBottom: '1.5rem', color: '#64748b', textAlign: 'center' }}>Transfer cash to Admin</p>

                        <form onSubmit={(e) => handleSendCash(e, true)}>

                            {/* --- NEW ADMIN DROPDOWN --- */}
                            <div className="form-group custom-dropdown-container" style={{ marginBottom: '1.25rem', textAlign: 'left' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Select Admin</label>

                                <div
                                    className={`custom-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    {selectedEmployeeObj ? (
                                        <div className="selected-emp-display">
                                            <div className="emp-avatar-mini"><User size={16} /></div>
                                            <span className="selected-emp-name">{selectedEmployeeObj.name}</span>
                                        </div>
                                    ) : (
                                        <span className="placeholder-text">-- Choose an Admin --</span>
                                    )}
                                    <ChevronDown size={20} className={`dropdown-arrow ${dropdownOpen ? 'rotated' : ''}`} />
                                </div>

                                {dropdownOpen && (
                                    <div className="custom-dropdown-menu">
                                        {adminDirectory.length === 0 ? (
                                            <div className="dropdown-empty">No admins found.</div>
                                        ) : (
                                            adminDirectory.map(admin => (
                                                <div
                                                    key={admin._id}
                                                    className={`custom-dropdown-item ${sendForm.toEmployeeId === admin._id ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setSendForm({ ...sendForm, toEmployeeId: admin._id });
                                                        setDropdownOpen(false);
                                                    }}
                                                >
                                                    <div className="emp-avatar-mini"><User size={16} /></div>
                                                    <div className="emp-drop-info">
                                                        <h4>{admin.name}</h4>
                                                        <span>{admin.phoneNumber}</span>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="form-group" style={{ marginBottom: '1.25rem', textAlign: 'left' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Amount (₹)</label>
                                <input
                                    type="number"
                                    className="emp-select-input"
                                    min="1"
                                    placeholder="0"
                                    value={sendForm.amount}
                                    onChange={e => setSendForm({ ...sendForm, amount: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group" style={{ marginBottom: '2rem', textAlign: 'left' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Reason / Note (Optional)</label>
                                <input
                                    type="text"
                                    className="emp-select-input"
                                    placeholder="e.g. End of day deposit"
                                    value={sendForm.reason}
                                    onChange={e => setSendForm({ ...sendForm, reason: e.target.value })}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="emp-btn-secondary" onClick={() => setShowAdminSendModal(false)}>Cancel</button>
                                <button type="submit" className="emp-btn-primary send-cash-btn" disabled={sending}>
                                    {sending ? 'Sending...' : 'Initiate Deposit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- INFO / SUCCESS MODAL --- */}
            {infoModal.show && (
                <div className="emp-modal-overlay" style={{ zIndex: 1200 }}>
                    <div className="emp-modal-card confirm-dialog-card">
                        <div className={`confirm-icon-wrapper ${infoModal.isError ? 'bg-red-light text-red' : 'bg-emerald-light text-emerald'}`}>
                            {infoModal.isError ? <XCircle size={36} /> : <CheckCircle size={36} />}
                        </div>
                        <h3>{infoModal.title}</h3>
                        <p>{infoModal.message}</p>

                        <div className="modal-actions" style={{ marginTop: '1.5rem', width: '100%' }}>
                            <button
                                type="button"
                                className={`emp-btn-primary ${infoModal.isError ? 'btn-danger-fill' : 'btn-success-fill'}`}
                                onClick={() => setInfoModal({ show: false, title: '', message: '', isError: false })}
                                style={{ width: '100%' }}
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashTransfers;